'use client'

import { IconLink, IconMoreHorizontal } from '@macarongfactory/web-icons'
import { BottomSheet, ListRow, Image } from '@macarongfactory/web-ui'
import { ReactNode, useRef } from 'react'

import {
  CAR_STORY_SHARE_CHANNEL,
  KAKAO_SHARE_BUTTON_IMAGE_URL,
} from '../lib/shareMeta'
import { carStoryTracking } from '../lib/tracking'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface ICarStoryContentDetailShareBottomSheetProps {
  contentId: number
  isOpen: boolean
  onClose: () => void
  onShareKakao: () => void
  onCopyUrl: () => void
  onShareMore: () => void
}

const IconContainer = ({ children }: { children: ReactNode }) => (
  <div className='w-dimension-x10 h-dimension-x10 rounded-full bg-background-neutral-weak flex items-center justify-center'>
    {children}
  </div>
)

const CarStoryContentDetailShareBottomSheet = ({
  contentId,
  isOpen,
  onClose,
  onShareKakao,
  onCopyUrl,
  onShareMore,
}: ICarStoryContentDetailShareBottomSheetProps) => {
  const handleKakao = () => {
    carStoryTracking.clickDetailShareButton(
      CAR_STORY_SHARE_CHANNEL.KAKAO,
      contentId
    )
    onShareKakao()
  }
  const handleLink = () => {
    carStoryTracking.clickDetailShareButton(
      CAR_STORY_SHARE_CHANNEL.LINK,
      contentId
    )
    onCopyUrl()
  }
  const handleMore = () => {
    carStoryTracking.clickDetailShareButton(
      CAR_STORY_SHARE_CHANNEL.MORE,
      contentId
    )
    onShareMore()
  }

  return (
    <BottomSheet
      open={isOpen}
      onClose={onClose}
      className='max-w-[480px] left-0 right-0 mx-auto'
      top={
        <BottomSheet.Top.LeftAlignedHeader
          title='주변 운전자에게 공유해 주세요!'
          titleTypography='heading-3-bold'
        />
      }
      content={
        <BottomSheet.Content>
          <div className='flex flex-col'>
            <ShareListRow
              contentId={contentId}
              channel={CAR_STORY_SHARE_CHANNEL.KAKAO}
              isOpen={isOpen}
              left={
                <Image
                  src={KAKAO_SHARE_BUTTON_IMAGE_URL}
                  alt='카카오톡 공유'
                  width={40}
                  height={40}
                  radius={0}
                  fallback={<Image.Fallback />}
                />
              }
              text='카카오톡으로 공유하기'
              onClick={handleKakao}
            />
            <ShareListRow
              contentId={contentId}
              channel={CAR_STORY_SHARE_CHANNEL.LINK}
              isOpen={isOpen}
              left={
                <IconContainer>
                  <IconLink fill='#1A2128' width={24} height={24} />
                </IconContainer>
              }
              text='공유 URL 복사하기'
              onClick={handleLink}
            />
            <ShareListRow
              contentId={contentId}
              channel={CAR_STORY_SHARE_CHANNEL.MORE}
              isOpen={isOpen}
              left={
                <IconContainer>
                  <IconMoreHorizontal fill='#1A2128' width={24} height={24} />
                </IconContainer>
              }
              text='공유 더보기'
              onClick={handleMore}
            />
          </div>
        </BottomSheet.Content>
      }
    />
  )
}

const ShareListRow = ({
  contentId,
  channel,
  isOpen,
  left,
  text,
  onClick,
}: {
  contentId: number
  channel: string
  isOpen: boolean
  left: ReactNode
  text: string
  onClick: () => void
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  useTrackOnVisible({
    ref,
    tracker: () => carStoryTracking.showDetailShareButton(channel, contentId),
    enabled: isOpen,
  })

  return (
    <div ref={ref}>
      <ListRow
        left={left}
        title={
          <ListRow.Title.Text
            text={text}
            typography='title-2-semibold'
            color='text-foreground-neutral-normal'
          />
        }
        verticalPadding='dimension-x4'
        clickable={onClick}
      />
    </div>
  )
}

export default CarStoryContentDetailShareBottomSheet
